import { RefObject, useState } from 'react';

import {
    Box,
    Button,
    FormControl,
    FormLabel,
    HStack,
    Input,
    InputGroup,
    InputRightElement,
    VStack
} from '@chakra-ui/react';
import { Formik } from 'formik';
import getConfig from 'next/config';
import { FormattedMessage, useIntl } from 'react-intl';

import {
    resetPassword,
    UserPasswordGotResetMessage
} from '~/components/User/UserPasswordResetForm';
import UserRegisterForm from '~/components/User/UserRegisterForm';
import { useFirebaseApp } from '~/lib/firebase';

const { publicRuntimeConfig } = getConfig();

const getEmailUserNotFound = (): string => {
    if (typeof window !== 'undefined' && sessionStorage) {
        return sessionStorage.getItem('auth/user-not-found') || '';
    }
    return '';
};

const removeEmailUserNotFound = (): void => {
    if (typeof window !== 'undefined' && sessionStorage) {
        return sessionStorage.removeItem('auth/user-not-found');
    }
};

interface IUserLoginFormComposition {
    getEmailUserNotFound: () => string;
    removeEmailUserNotFound: () => void;
}

const UserLoginForm: React.FunctionComponent<{
    initialFocusRef?: RefObject<HTMLInputElement> | undefined;
    openPasswordResetForm: () => void;
}> &
    IUserLoginFormComposition = ({ initialFocusRef, openPasswordResetForm }) => {
    const [isPasswordVisible, showPassword] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [resetEmail, setResetEmail] = useState<string | null>(null);
    const [passwordGotReset, setPasswordGotReset] = useState(false);
    const { app } = useFirebaseApp();
    const emailAlreadyExists = UserRegisterForm.getEmailAlreadyExists();
    const intl = useIntl();

    if (passwordGotReset) {
        return <UserPasswordGotResetMessage />;
    }

    return (
        <Box>
            <Formik
                initialValues={{
                    email: emailAlreadyExists,
                    password: ''
                }}
                onSubmit={async (values, { setSubmitting }) => {
                    setSubmitError(null);

                    try {
                        await fetch(`${publicRuntimeConfig.basePath}/api/account/pre-login`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json'
                            },
                            body: JSON.stringify({
                                email: values.email,
                                password: values.password
                            })
                        });
                    } catch (err) {
                        console.error(err);
                    }

                    app.auth()
                        .signInWithEmailAndPassword(values.email, values.password)
                        .then(() => {
                            location.reload();
                        })
                        .catch((e) => {
                            if (e.code === 'auth/wrong-password') {
                                setSubmitError(
                                    intl.formatMessage({
                                        id: 'user.onboarding.login_form.error.wrong_password',
                                        defaultMessage: 'Das Passwort ist falsch.'
                                    })
                                );
                                sessionStorage.setItem('auth/email-already-exists', values.email);
                                setResetEmail(values.email);
                            } else if (e.code === 'auth/user-not-found') {
                                setSubmitError(
                                    intl.formatMessage({
                                        id: 'user.onboarding.login_form.error.user_not_found',
                                        defaultMessage: 'Sie sind noch nicht registriert.'
                                    })
                                );
                            } else {
                                setSubmitError(e.message);
                            }
                        })
                        .finally(() => setSubmitting(false));
                }}
            >
                {({ values, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
                    <form onSubmit={handleSubmit}>
                        <VStack align="stretch" spacing="4">
                            <Box>
                                <FormControl id="email" width="full" isRequired>
                                    <FormLabel>
                                        <FormattedMessage
                                            id="user.onboarding.login_form.field_email.label"
                                            defaultMessage="E-Mail Adresse"
                                        />
                                    </FormLabel>
                                    <Input
                                        ref={initialFocusRef}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.email}
                                        type="email"
                                    />
                                </FormControl>
                            </Box>
                            <Box>
                                <FormControl id="password" width="full" isRequired>
                                    <HStack justify="space-between">
                                        <FormLabel>
                                            <FormattedMessage
                                                id="user.onboarding.login_form.field_password.label"
                                                defaultMessage="Passwort"
                                            />
                                        </FormLabel>
                                        <Button
                                            onClick={openPasswordResetForm}
                                            variant="link"
                                            colorScheme="brand"
                                            size="xs"
                                        >
                                            <FormattedMessage
                                                id="user.onboarding.login_form.password_forgotten"
                                                defaultMessage="Passwort vergessen?"
                                            />
                                        </Button>
                                    </HStack>

                                    <InputGroup size="md">
                                        <Input
                                            pr="7rem"
                                            type={isPasswordVisible ? 'text' : 'password'}
                                            placeholder={intl.formatMessage({
                                                id: 'user.onboarding.login_form.field_password.placeholder',
                                                defaultMessage: 'Enter password'
                                            })}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values.password}
                                        />
                                        <InputRightElement width="7rem" mr="-2">
                                            <Button
                                                h="1.75rem"
                                                size="sm"
                                                onClick={() => showPassword(!isPasswordVisible)}
                                            >
                                                {isPasswordVisible ? (
                                                    <FormattedMessage
                                                        id="user.onboarding.login_form.field_password.visibility.hide"
                                                        defaultMessage="Verstecken"
                                                    />
                                                ) : (
                                                    <FormattedMessage
                                                        id="user.onboarding.login_form.field_password.visibility.show"
                                                        defaultMessage="Anzeigen"
                                                    />
                                                )}
                                            </Button>
                                        </InputRightElement>
                                    </InputGroup>
                                </FormControl>
                            </Box>
                            <Box textAlign="center">
                                {submitError}
                                {resetEmail ? (
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        colorScheme="red"
                                        onClick={() => {
                                            resetPassword(app, resetEmail).then(() =>
                                                setPasswordGotReset(true)
                                            );
                                        }}
                                        my="2"
                                        py="2"
                                        height="auto"
                                        whiteSpace="normal"
                                    >
                                        <FormattedMessage
                                            id="user.onboarding.login_form.reset_password_button"
                                            defaultMessage="Neues Passwort an {email} senden"
                                            values={{
                                                email: resetEmail
                                            }}
                                        />
                                    </Button>
                                ) : null}
                            </Box>
                            <Box textAlign="center" paddingTop="2">
                                <Button
                                    type="submit"
                                    disabled={isSubmitting}
                                    variant="solid"
                                    colorScheme="brand"
                                >
                                    <FormattedMessage
                                        id="user.onboarding.login_form.submit_button"
                                        defaultMessage="Einloggen"
                                    />
                                </Button>
                            </Box>
                        </VStack>
                    </form>
                )}
            </Formik>
        </Box>
    );
};

UserLoginForm.getEmailUserNotFound = getEmailUserNotFound;
UserLoginForm.removeEmailUserNotFound = removeEmailUserNotFound;

export default UserLoginForm;
